/**
 * Everything about a hub that is not worth a permanent place on its card.
 *
 * The card header is a glance: is this hub alive, warm, charged, and coming
 * back when it said it would. Everything else — the 4G kit, the branches it
 * deploys from, how often it updates its code — is looked at deliberately, and
 * only ever a few hubs at a time, so it lives one click away behind the hub's
 * own icon instead of crowding the row of every hub on the page.
 *
 * One Save for the whole dialog: the sections write different keys, but one
 * button per section read as saving everything and saved only its own. What
 * has changed goes in a single request.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HubSettingsSource } from '@/services/field.service';
import { useUpdateHubSettings, type HubSettings } from '@/services/hub.service';
import {
    MobileFields,
    iccidDigits,
    iccidValid,
    mobileOf,
    type Mobile,
} from './HubSim';
import {
    BranchFields,
    branchChanged,
    branchValid,
    branchesOf,
} from './HubBranches';
import { saveErrorMessage } from './saveError';

/** What the backend will accept for `code_update_interval_days`. */
const MIN_UPDATE_DAYS = -1;
const MAX_UPDATE_DAYS = 365;

/**
 * The two values that are not a number of days, as a unit read after the
 * number being typed: the hub reads -1 as "never update" and 0 as "update on
 * every wake-up", and "-1 d" or "0 d" would read as nonsense.
 */
function unitLabel(days: number): string {
    if (days === -1) return '= never';
    if (days === 0) return '= each wake-up';
    return 'd';
}

export default function HubDetails({
    hub,
    onClose,
    onDirtyChange,
}: {
    hub: HubSettingsSource;
    onClose: () => void;
    /** Whether the dialog holds an edit that is neither saved nor cancelled. */
    onDirtyChange: (dirty: boolean) => void;
}) {
    const save = useUpdateHubSettings();

    // The 4G kit: our notes. Every field goes when any changed, empty
    // included: an emptied field is a deliberate "we no longer know this",
    // which the backend stores as null.
    const currentMobile = mobileOf(hub);
    const [mobile, setMobile] = useState<Mobile>(currentMobile);
    const mobileChanged =
        mobile.operator.trim() !== currentMobile.operator ||
        iccidDigits(mobile.iccid) !== currentMobile.iccid ||
        mobile.modem.trim() !== currentMobile.modem;

    // The branches: only the halves that moved.
    const currentBranches = branchesOf(hub);
    const [branches, setBranches] = useState({
        pi: currentBranches.pi ?? '',
        ansible: currentBranches.ansible ?? '',
    });
    const piChanged = branchChanged(branches.pi, currentBranches.pi);
    const ansibleChanged = branchChanged(
        branches.ansible,
        currentBranches.ansible,
    );

    // The code update interval. Empty on a hub whose config lacks the key,
    // which leaves it alone.
    const [intervalDraft, setIntervalDraft] = useState(
        hub.codeUpdateIntervalDays != null
            ? String(hub.codeUpdateIntervalDays)
            : '',
    );
    const days = Number.parseInt(intervalDraft, 10);
    const intervalValid =
        intervalDraft.trim() === '' ||
        (Number.isFinite(days) &&
            days >= MIN_UPDATE_DAYS &&
            days <= MAX_UPDATE_DAYS);
    const intervalChanged =
        intervalDraft.trim() !== '' && days !== hub.codeUpdateIntervalDays;

    const changed =
        mobileChanged || piChanged || ansibleChanged || intervalChanged;
    const valid =
        iccidValid(mobile.iccid) &&
        branchValid(branches.pi) &&
        branchValid(branches.ansible) &&
        intervalValid;

    const report = useRef(onDirtyChange);
    report.current = onDirtyChange;
    useEffect(() => report.current(changed), [changed]);

    const commit = () => {
        if (!valid || save.isPending) return;
        if (!changed) {
            onClose();
            return;
        }
        const settings: HubSettings = {};
        if (mobileChanged) {
            settings.simOperator = mobile.operator.trim();
            settings.simIccid = iccidDigits(mobile.iccid);
            settings.modem = mobile.modem.trim();
        }
        if (piChanged) settings.scanorhizePiBranch = branches.pi.trim();
        if (ansibleChanged)
            settings.humeosAnsibleBranch = branches.ansible.trim();
        if (intervalChanged) settings.codeUpdateIntervalDays = days;
        save.mutate(
            { macAddress: hub.macAddress, settings },
            { onSuccess: onClose },
        );
    };
    // Escape in a field closes only an untouched dialog: a changed one is
    // saved or cancelled with the buttons, never dropped by a stray key.
    const escape = () => {
        if (!changed) onClose();
    };

    return (
        <div className="flex flex-col gap-6">
            <Section
                title="Mobile connection"
                hint="The SIM we fitted and what the modem says it is actually on."
            >
                <MobileFields
                    hub={hub}
                    value={mobile}
                    onChange={setMobile}
                    onEnter={commit}
                    onEscape={escape}
                />
            </Section>

            <Section
                title="Deployment branches"
                hint="Which code this hub pulls, and which ansible-pull branch provisions it."
            >
                <BranchFields
                    current={currentBranches}
                    pi={branches.pi}
                    ansible={branches.ansible}
                    onChange={setBranches}
                    onEnter={commit}
                    onEscape={escape}
                />
            </Section>

            <Section
                title="Code updates"
                hint="How many days the hub waits between code updates: -1 never updates, 0 updates on every wake-up."
            >
                <div className="flex items-center gap-1.5">
                    <input
                        type="number"
                        min={MIN_UPDATE_DAYS}
                        max={MAX_UPDATE_DAYS}
                        value={intervalDraft}
                        placeholder="—"
                        onChange={(event) =>
                            setIntervalDraft(event.target.value)
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') commit();
                            else if (event.key === 'Escape') escape();
                        }}
                        className={
                            'h-7 w-16 px-1.5 rounded text-sm tabular-nums bg-[#0f141a] border text-[#d8dde2] focus:outline-none [color-scheme:dark] ' +
                            (intervalValid
                                ? 'border-[#2d2d2d] focus:border-[#74c69d]'
                                : 'border-[#d9534f]')
                        }
                    />
                    {/* The unit says what the number being typed means, so -1
                        and 0 announce themselves before they are saved. */}
                    <span className="text-xs text-[#6b7480] whitespace-nowrap">
                        {Number.isFinite(days) && intervalValid
                            ? unitLabel(days)
                            : 'd'}
                    </span>
                </div>
            </Section>

            {save.isError && (
                <p className="text-sm text-[#ff8a84]">
                    Not saved — {saveErrorMessage(save.error)}
                </p>
            )}

            <div className="flex justify-end gap-2 pt-1 border-t border-[#1e242a]">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={onClose}
                    className="mt-3 bg-transparent border-[#1e242a] text-[#a8b0bb] hover:bg-[#1a2126] hover:text-white"
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    disabled={save.isPending || !valid || !changed}
                    onClick={commit}
                    className="mt-3 bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                >
                    {save.isPending && (
                        <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    )}
                    Save
                </Button>
            </div>
        </div>
    );
}

function Section({
    title,
    hint,
    children,
}: {
    title: string;
    hint: string;
    children: React.ReactNode;
}) {
    return (
        <section className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
                <h3 className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                    {title}
                </h3>
                <p className="text-[11px] text-[#6b7480]">{hint}</p>
            </div>
            <div className="pt-1 border-t border-[#1e242a]">
                <div className="pt-3">{children}</div>
            </div>
        </section>
    );
}
